import { FC, useMemo } from 'react';
import useImage from 'use-image';
import { Image, Layer, Stage } from 'react-konva';
import { DefaultCartModelDisplay } from '../CartModelDisplay';
import CartModelDisplayLoading from '../CartModelDisplayLoading/CartModelDisplayLoading';
import useCartModelDisplay from '../../../hooks/useCartModelDisplay';
import { Wheels12In, Wheels12InLifted } from '../../../data/products/Carts/ESeriesProducts/Shared/Wheels';

interface E4ModelProps {
  defaults?: DefaultCartModelDisplay,
  width?: number,
  height?: number
}

const E4Model: FC<E4ModelProps> = ({
  defaults,
  width = 1000,
  height = 1000
}) => {
  const { chassisType, bodyColor, liftKit, wheel, frontSeat, rearSeat } = useCartModelDisplay();

  const defaultChassisImage = chassisType?.liftKitOptions[1].configuratorImg ?? '';
  const chassisImageUrl = useMemo<String>(() => {
    let chassisImageUrl: String = '';
    if (defaults) {
      chassisImageUrl = defaults.liftKit?.configuratorImg ?? '';
    } else {
      chassisImageUrl = liftKit?.configuratorImg ?? defaultChassisImage;
    }
    return chassisImageUrl;
  }, [defaults, liftKit, defaultChassisImage]);

  const liftKitSku = liftKit?.sku;
  const wheelTireImageUrl = useMemo<String>(() => {
    let wheelTireImageUrl: String = '';
    if (defaults) {
      wheelTireImageUrl = defaults.wheel?.configuratorImg ?? '';
    } else {
      if (wheel?.configuratorImg) {
        wheelTireImageUrl = wheel?.configuratorImg;
      } else {
        wheelTireImageUrl =
          liftKitSku === 'L'
            ? Wheels12InLifted[0].configuratorImg ?? ''
            : Wheels12In[0].configuratorImg ?? ''
      };
    }
    return wheelTireImageUrl;
  }, [defaults, wheel, liftKitSku]);

  const defaultBodyColorImage = chassisType?.bodyColorOptions[0].configuratorImg ?? '';
  const bodyColorImageUrl = useMemo<String>(() => {
    let bodyColorImageUrl: String = '';
    if (defaults) {
      bodyColorImageUrl = defaults.bodyColor?.configuratorImg ?? '';
    } else {
      bodyColorImageUrl = bodyColor?.configuratorImg ?? defaultBodyColorImage;
    }
    return bodyColorImageUrl;
  }, [defaults, bodyColor, defaultBodyColorImage]);

  const fenderFlaresImageUrl = useMemo<String>(() => {
    let fenderFlareImageUrl: String = '';
    if (defaults) {
      fenderFlareImageUrl = defaults.liftKit?.sku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/a714d6c9-295a-49a5-5fd3-d8b6eac97600'
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700'
    } else {
      fenderFlareImageUrl = liftKitSku === 'L'
        ? 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/a714d6c9-295a-49a5-5fd3-d8b6eac97600'
        : 'https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/f41bdf4c-32b5-43a8-6f7f-aca342fb5700' // Empty image
    }
    return fenderFlareImageUrl;
  }, [defaults, liftKitSku]);

  const rearStrutsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/ae0f414e-6747-40d9-d630-918b74eaf200";
  const sideMirrorsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/9c792e71-2884-4923-dcd1-91d4e67ee900";

  const bodyColorRef = bodyColor?.refId;
  const frontSeatImageUrl = useMemo<String>(() => {
    let frontSeatImageUrl = '';
    const matchingFrontSeatOption = chassisType?.frontSeatOptions.find((seat) => seat.refId === bodyColorRef);
    if (defaults) {
      frontSeatImageUrl = defaults.frontSeat?.configuratorImg ?? '';
    } else {
      if (frontSeat?.configuratorImg) {
        frontSeatImageUrl = frontSeat?.configuratorImg ?? '';
      } else {
        frontSeatImageUrl =
          matchingFrontSeatOption?.configuratorImg ??
          chassisType?.frontSeatOptions[0].configuratorImg ?? ''
      }
    }
    return frontSeatImageUrl;
  }, [defaults, chassisType, frontSeat, bodyColorRef]);

  const rearSeatImageUrl = useMemo<String>(() => {
    let rearSeatImageUrl = '';
    const matchingRearSeatOption = chassisType?.rearSeatOptions?.find((seat) => seat.refId === bodyColorRef);
    if (defaults) {
      rearSeatImageUrl = defaults.rearSeat?.configuratorImg ?? '';
    } else {
      if (rearSeat?.configuratorImg) {
        rearSeatImageUrl = rearSeat?.configuratorImg ?? '';
      } else {
        if (chassisType?.rearSeatOptions) {
          rearSeatImageUrl =
            matchingRearSeatOption?.configuratorImg ??
            chassisType?.rearSeatOptions[0].configuratorImg ?? ''
        }
      }
    }
    return rearSeatImageUrl;
  }, [defaults, chassisType, rearSeat, bodyColorRef]);

  const steeringWheelImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/8c74e311-6019-4259-858d-7c1477b9e500";
  const windshieldImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/d4273216-4cff-4ee6-775a-57fa255bcf00";
  const topImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/d7882b03-6f9f-4ddb-50a7-71c9e4cb8b00";
  const decalsImageUrl = "https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/16f6ba4d-4376-40c7-d0ed-0d86a056c800";

  const [chassisImage, chassisImageStatus] = useImage(`${chassisImageUrl}/w=${width}`);
  const [wheelTireImage, wheelTireImageStatus] = useImage(`${wheelTireImageUrl}/w=${width}`);
  const [bodyColorImage, bodyColorImageStatus] = useImage(`${bodyColorImageUrl}/w=${width}`);
  const [fenderFlareImage, fenderFlareImageStatus] = useImage(`${fenderFlaresImageUrl}/w=${width}`);
  const [rearStrutsImage, rearStrutsImageStatus] = useImage(`${rearStrutsImageUrl}/w=${width}`);
  const [sideMirrorsImage, sideMirrorsImageStatus] = useImage(`${sideMirrorsImageUrl}/w=${width}`);
  const [frontSeatImage, frontSeatImageStatus] = useImage(`${frontSeatImageUrl}/w=${width}`);
  const [rearSeatImage, rearSeatImageStatus] = useImage(`${rearSeatImageUrl}/w=${width}`);
  const [steeringWheelImage, steeringWheelImageStatus] = useImage(`${steeringWheelImageUrl}/w=${width}`);
  const [windshieldImage, windshieldImageStatus] = useImage(`${windshieldImageUrl}/w=${width}`);
  const [topImage, topImageStatus] = useImage(`${topImageUrl}/w=${width}`);
  const [decals, decalsImageStatus] = useImage(`${decalsImageUrl}/w=${width}`);

  // Center Image on canvas
  const x = useMemo<number>(() => {
    let x: number = 0;
    if (chassisImage !== undefined) {

      /* after the image is loaded, you can get it's dimensions */
      const imageWidth = chassisImage?.width;

      /* 
        calculate the offsets so the image is centered inside
        the canvas
      */
      const xOffset = (width - imageWidth) / 2;
      x = xOffset;
    }
    return x;
  }, [width, chassisImage]);

  const y = useMemo<number>(() => {
    let y: number = 0;
      if (chassisImage !== undefined) {

        /* after the image is loaded, you can get it's dimensions */
        const imageHeight = chassisImage?.height;

        /* 
          calculate the offsets so the image is centered inside
          the canvas
        */
        const yOffset = (height - imageHeight) / 2;
        y = yOffset;
      }
    return y;
  }, [height, chassisImage]);

  const loading =
    chassisImageStatus === 'loading' ||
    wheelTireImageStatus === 'loading' ||
    bodyColorImageStatus === 'loading' ||
    fenderFlareImageStatus === 'loading' ||
    rearStrutsImageStatus === 'loading' ||
    sideMirrorsImageStatus === 'loading' ||
    frontSeatImageStatus === 'loading' ||
    rearSeatImageStatus === 'loading' ||
    steeringWheelImageStatus === 'loading' ||
    windshieldImageStatus === 'loading' ||
    topImageStatus === 'loading' ||
    decalsImageStatus === 'loading';

  return (
    <CartModelDisplayLoading
      width={width}
      height={height}
      loading={loading}>
      <Stage
        width={width}
        height={height}>
        <Layer>
          <Image image={chassisImage} x={x} y={y} />
          <Image image={wheelTireImage} x={x} y={y} />
          <Image image={bodyColorImage} x={x} y={y} />
          <Image image={rearSeatImage} x={x} y={y} />
          <Image image={rearStrutsImage} x={x} y={y} />
          <Image image={sideMirrorsImage} x={x} y={y} />
          <Image image={frontSeatImage} x={x} y={y} />
          <Image image={steeringWheelImage} x={x} y={y} />
          <Image image={fenderFlareImage} x={x} y={y} />
          <Image image={windshieldImage} x={x} y={y} />
          <Image image={topImage} x={x} y={y} />
          <Image image={decals} x={x} y={y} />
        </Layer>
      </Stage>
    </CartModelDisplayLoading>
  );
}

export default E4Model;
